import {z} from 'zod';

import {
  BasePriceSchema,
  BasketTooltipSchema,
  PriceWithAndWithoutVatSchema,
  AbortSignalSchema,
  BaseVoidResponseSchema,
  CreatedAndUpdatedBySchema,
  GetArticleResponseSchema,
} from '@dms/api/shared';

/**
 * Base types
 */
const BaseSupplierOrderSchema = z
  .object({
    orderId: z.string(),
    orderNumber: z.string(),
    orderExternalNumber: z.string(),
    supplierId: z.string(),
    state: z.string(),
    orderVariantId: z.string(),
    type: z.string(),
    supplierAccountCode: z.string().nullish(),
    completedAt: z.string(),
    orderedAt: z.string(),
    referenceNumber: z.string(),
    warehouseId: z.string(),
    currency: z.string(),
    exchangeRate: z.number(),
    ncConversion: z.string(),
    note: z.string().nullish(),
    // Supplier response fields (populated only for integrated orders after a state check).
    supplierOrderState: z.string().nullish(),
    supplierStateCheckedAt: z.string().nullish(),
  })
  .merge(CreatedAndUpdatedBySchema);

export type BaseSupplierOrder = z.infer<typeof BaseSupplierOrderSchema>;

const TotalPriceWithAndWithoutVatSchema = z.object({
  totalWithoutVat: BasePriceSchema,
  totalWithVat: BasePriceSchema,
});

export type TotalPriceWithAndWithoutVat = z.infer<typeof TotalPriceWithAndWithoutVatSchema>;

export const BaseSupplierOrderMaterialItemSchema = z.object({
  id: z.string(),
  warehouse_id: z.string(),
  itemEditingAllowed: z.boolean(),
  priceType: z.string(),
  requestType: z.string(),
  name: z.string(),
  number: z.string(),
  quantity: z.number(),
  unit: z.string(),
  dispensingUnit: z.number(),
  quantityEditingAllowed: z.boolean(),
  unitPrice: PriceWithAndWithoutVatSchema,
  discount: z.object({rate: z.number()}).merge(PriceWithAndWithoutVatSchema),
  totalPrice: PriceWithAndWithoutVatSchema,
  tooltip: z.array(BasketTooltipSchema),
});
export type BaseSupplierOrderMaterialItem = z.infer<typeof BaseSupplierOrderMaterialItemSchema>;

/**
 * GET requests
 */
export const GetSupplierOrderRequestSchema = z.object({
  orderId: z.string(),
});
export type GetSupplierOrderRequest = z.infer<typeof GetSupplierOrderRequestSchema>;

export const GetSupplierOrderResponseSchema = z.object({
  order: BaseSupplierOrderSchema,
  totals: TotalPriceWithAndWithoutVatSchema,
});
export type GetSupplierOrderResponse = z.infer<typeof GetSupplierOrderResponseSchema>;

export const GetSupplierOrderItemRequestSchema = z.object({
  orderId: z.string(),
  itemId: z.string(),
});
export type GetSupplierOrderItemRequest = z.infer<typeof GetSupplierOrderItemRequestSchema>;

export const GetSupplierOrderItemResponseSchema = z.object({
  orderItemId: z.string(),
  orderId: z.string(),
  orderDetail: z.object({
    supplierQuantity: z.number(),
    supplierUnit: z.string(),
    supplierUnitPrice: z.number(),
    supplierTotalPrice: z.number(),
    name: z.string(),
    catalogueNumber: z.string(),
    manufacturerId: z.string(),
    supplierOrderingNumber: z.string(),
    vat: z.string(),
    vin: z.string(),
    mileage: z.number().nullish(),
    keyCode: z.string(),
    isInteligentPart: z.boolean(),
    currency: z.string(),
    engineNumber: z.string().nullish(),
    labelInfo: z.string().nullish(),
    serialNumber: z.number().nullish(),
    note: z.string().nullish(),
    location: z.string().nullish(),
    engineCode: z.string().nullish(),
    referenceNumber: z.string().nullish(),
    // Supplier response fields (populated only for integrated orders after a state check).
    supplierAcceptedQuantity: z.number().nullish(),
    supplierMissingQuantity: z.number().nullish(),
    supplierItemState: z.string().nullish(),
  }),
  requestDetail: z.object({
    warehouseRequestedQuantity: z.number(),
    warehouseUnitPurchasePrice: z.number(),
  }),
  supplierArticle: z.object({
    supplierQuantity: z.number(),
    supplierUnitId: z.string(),
    warehouseQuantity: z.number(),
    supplierOrderingNumber: z.string().optional(),
    supplierBulkPackageQuantity: z.number().optional(),
    supplierId: z.string(),
    supplierDiscountGroupCode: z.string().optional(),
  }),
  article: GetArticleResponseSchema,
});
export type GetSupplierOrderItemResponse = z.infer<typeof GetSupplierOrderItemResponseSchema>;

export const GetSupplierOrderMaterialBasketRequestSchema = z.object({orderId: z.string()});
export type GetSupplierOrderMaterialBasketRequest = z.infer<
  typeof GetSupplierOrderMaterialBasketRequestSchema
>;

export const GetSupplierOrderMaterialBasketResponseSchema = z.object({
  sparePartsBasketItem: z.array(BaseSupplierOrderMaterialItemSchema),
  sparePartsBasketTotalPrice: PriceWithAndWithoutVatSchema,
});
export type GetSupplierOrderMaterialBasketResponse = z.infer<
  typeof GetSupplierOrderMaterialBasketResponseSchema
>;

export const SupplierOrderTypeSchema = z.object({
  label: z.string().nullish(),
  value: z.string().nullish(),
  coefficient: z.number().nullish(),
});
export type SupplierOrderType = z.infer<typeof SupplierOrderTypeSchema>;

export const SupplierAccountSchema = z.object({
  label: z.string().nullish(),
  value: z.string().nullish(),
  userId: z.string().nullish(),
  branchId: z.string().nullish(),
  systemName: z.string().nullish(),
});
export type SupplierAccount = z.infer<typeof SupplierAccountSchema>;

export const GetSupplierOrderTypeRequestSchema = z.object({
  supplierOrderVariantId: z.string(),
  supplierId: z.string(),
});
export type GetSupplierOrderTypeRequest = z.infer<typeof GetSupplierOrderTypeRequestSchema>;

// The backend may return null for the whole body (or omit/null the individual fields) when a
// supplier has no order types or accounts; normalize to an object with empty arrays so consumers
// always get a list and never have to guard against a null response.
export const GetSupplierOrderTypeResponseSchema = z
  .object({
    orderType: z.array(SupplierOrderTypeSchema).nullish(),
    supplierAccount: z.array(SupplierAccountSchema).nullish(),
  })
  .nullish()
  .transform((value) => ({
    orderType: value?.orderType ?? [],
    supplierAccount: value?.supplierAccount ?? [],
  }));
export type GetSupplierOrderTypeResponse = z.infer<typeof GetSupplierOrderTypeResponseSchema>;

// v3 of GET supplier-order/type. Unlike v2, the order variant is optional and an authorization
// profile can be provided; the active branch is sent automatically as the x-branch header by the
// base query. This lets consumers outside of a supplier order (price list, workshop) resolve both
// order types and supplier accounts without faking an order variant. The response shape is
// identical to v2, so GetSupplierOrderTypeResponseSchema is reused.
export const GetSupplierOrderTypeV3RequestSchema = z.object({
  supplierId: z.string(),
  supplierOrderVariantId: z.string().optional(),
  authorizationProfileId: z.string().optional(),
});
export type GetSupplierOrderTypeV3Request = z.infer<typeof GetSupplierOrderTypeV3RequestSchema>;

/**
 * POST requests
 */
export const PostSupplierOrderRequestSchema = z.object({
  orderVariantId: z.string(),
  supplierId: z.string(),
  orderType: z.string().nullish(),
  supplierAccountCode: z.string().nullish(),
});
export type PostSupplierOrderRequest = z.infer<typeof PostSupplierOrderRequestSchema>;

export const PostSupplierOrderResponseSchema = z.object({
  orderId: z.string().nullish(),
});
export type PostSupplierOrderResponse = z.infer<typeof PostSupplierOrderResponseSchema>;

export const BulkPostSupplierOrderRequestSchema = z
  .object({
    requestItemId: z.array(z.string()),
  })
  .merge(PostSupplierOrderRequestSchema);
export type BulkPostSupplierOrderRequest = z.infer<typeof BulkPostSupplierOrderRequestSchema>;

export const BulkPostSupplierOrderResponseSchema = z
  .object({
    orderItemId: z.array(z.string()),
  })
  .merge(PostSupplierOrderResponseSchema);
export type BulkPostSupplierOrderResponse = z.infer<typeof BulkPostSupplierOrderResponseSchema>;

export const PostServiceOrderSupplierOrdersRequestSchema = z.object({
  serviceOrderId: z.string(),
  // Not sent to the backend; it only names the service case whose cache the mutation invalidates.
  serviceCaseId: z.string(),
  body: z.object({
    // One supplier order per warehouse; the backend derives the warehouse from the order variant.
    supplierOrders: z.array(
      z.object({
        orderVariantId: z.string(),
        supplierId: z.string(),
        orderType: z.string().nullish(),
        supplierAccount: z.string().nullish(),
      })
    ),
  }),
});
export type PostServiceOrderSupplierOrdersRequest = z.infer<
  typeof PostServiceOrderSupplierOrdersRequestSchema
>;

export const PostServiceOrderSupplierOrdersResponseSchema = z.object({
  rows: z.array(
    z.object({
      orderId: z.string(),
      orderItemIds: z.array(z.string()),
    })
  ),
});
export type PostServiceOrderSupplierOrdersResponse = z.infer<
  typeof PostServiceOrderSupplierOrdersResponseSchema
>;

export const BulkPostSupplierOrderItemsRequestSchema = z.object({
  orderId: z.string(),
  body: z.object({
    requestItemId: z.array(z.string()),
    itemState: z.union([z.literal('BASKET'), z.literal('ORDER')]),
  }),
});
export type BulkPostSupplierOrderItemsRequest = z.infer<
  typeof BulkPostSupplierOrderItemsRequestSchema
>;

export const BulkPostSupplierOrderItemsResponseSchema = z.object({
  orderItemId: z.array(z.string()),
});
export type BulkPostSupplierOrderItemsResponse = z.infer<
  typeof BulkPostSupplierOrderItemsResponseSchema
>;

export const PostSupplierOrderMaterialBasketItemRequestSchema = z.object({
  orderId: z.string(),
  body: z.object({
    article: z.array(
      z.object({
        articleId: z.string(),
        toOrderQuantity: z.number(),
      })
    ),
  }),
});
export type PostSupplierOrderMaterialBasketItemRequest = z.infer<
  typeof PostSupplierOrderMaterialBasketItemRequestSchema
>;

export const PostSupplierOrderMaterialBasketItemResponseSchema = z.object({
  orderItemId: z.string(),
});
export type PostSupplierOrderMaterialBasketItemResponse = z.infer<
  typeof PostSupplierOrderMaterialBasketItemResponseSchema
>;

export const PostSupplierOrderBasketCheckoutRequestSchema = z.object({orderId: z.string()});
export type PostSupplierOrderBasketCheckoutRequest = z.infer<
  typeof PostSupplierOrderBasketCheckoutRequestSchema
>;

export const PostSupplierOrderBasketCheckoutResponseSchema = z.object({
  requestItemId: z.array(z.object({id: z.string().nullish()})),
});
export type PostSupplierOrderBasketCheckoutResponse = z.infer<
  typeof PostSupplierOrderBasketCheckoutResponseSchema
>;

/**
 * PATCH request
 */
export const PatchSupplierOrderRequestSchema = z.object({
  orderId: z.string(),
  body: z.object({
    warehouseId: z.string().nullable(),
    orderVariantId: z.string(),
    supplierId: z.string().nullable(),
    orderExternalNumber: z.string().nullish(),
    referenceNumber: z.string().nullish(),
    type: z.string().nullish(),
    supplierAccountCode: z.string().nullish(),
    currency: z.string().nullish(),
    exchangeRate: z.number().nullish(),
    ncConversion: z.string().nullish(),
    note: z.string().nullish(),
  }),
});
export type PatchSupplierOrderRequest = z.infer<typeof PatchSupplierOrderRequestSchema>;

export const PatchSupplierOrderResponseSchema = BaseVoidResponseSchema;
export type PatchSupplierOrderResponse = z.infer<typeof PatchSupplierOrderResponseSchema>;

export const PatchSupplierOrderStateRequestSchema = z.object({
  orderId: z.string(),
  body: z.object({
    orderState: z.union([z.literal('PENDING'), z.literal('CLOSED'), z.literal('SENT')]),
  }),
});
export type PatchSupplierOrderStateRequest = z.infer<typeof PatchSupplierOrderStateRequestSchema>;

export const PatchSupplierOrderStateResponseSchema = BaseVoidResponseSchema;
export type PatchSupplierOrderStateResponse = z.infer<typeof PatchSupplierOrderStateResponseSchema>;

export const PatchSupplierOrderItemRequestSchema = z.object({
  orderId: z.string(),
  itemId: z.string(),
  body: z.object({
    article: z.object({id: z.string()}),
    orderDetail: z.object({
      supplierQuantity: z.number(),
      supplierUnit: z.string(),
      supplierUnitPrice: z.number(),
      supplierTotalPrice: z.number(),
      supplierOrderingNumber: z.string().nullish(),
      vat: z.string(),
      vin: z.string().nullish(),
      mileage: z.number().nullish(),
      keyCode: z.string().nullish(),
      isIntelligentPart: z.boolean(),
      currency: z.string(),
      engineNumber: z.string().nullish(),
      labelInfo: z.string().nullish(),
      note: z.string().nullish(),
      location: z.string().nullish(),
      engineCode: z.string().nullish(),
    }),
    requestDetail: z.object({
      warehouseRequestedQuantity: z.number(),
      warehouseUnitPurchasePrice: z.number(),
    }),
    supplierArticle: z.object({
      supplierQuantity: z.number(),
      supplierUnitId: z.string(),
      warehouseQuantity: z.number(),
      supplierOrderingNumber: z.string().optional(),
      supplierBulkPackageQuantity: z.number().optional(),
      isUpdateMapping: z.boolean(),
    }),
  }),
});
export type PatchSupplierOrderItemRequest = z.infer<typeof PatchSupplierOrderItemRequestSchema>;

export const PatchSupplierOrderItemResponseSchema = BaseVoidResponseSchema;
export type PatchSupplierOrderItemResponse = z.infer<typeof PatchSupplierOrderItemResponseSchema>;

export const PatchSupplierOrderMaterialBasketItemQuantityRequestSchema = z.object({
  orderId: z.string(),
  itemId: z.string(),
  body: z.object({quantity: z.number()}),
});
export type PatchSupplierOrderMaterialBasketItemQuantityRequest = z.infer<
  typeof PatchSupplierOrderMaterialBasketItemQuantityRequestSchema
>;

export const PatchSupplierOrderMaterialBasketItemQuantityResponseSchema = BaseVoidResponseSchema;
export type PatchSupplierOrderMaterialBasketItemQuantityResponse = z.infer<
  typeof PatchSupplierOrderMaterialBasketItemQuantityResponseSchema
>;

/**
 * DELETE requests
 */
export const DeleteSupplierOrderRequestSchema = z.object({
  body: z.object({
    orderId: z.array(z.string()),
  }),
});
export type DeleteSupplierOrderRequest = z.infer<typeof DeleteSupplierOrderRequestSchema>;

export const DeleteSupplierOrderResponseSchema = BaseVoidResponseSchema;
export type DeleteSupplierOrderResponse = z.infer<typeof DeleteSupplierOrderResponseSchema>;

export const DeleteSupplierOrderItemsRequestSchema = z.object({
  orderId: z.string(),
  body: z.object({
    itemId: z.array(z.string()),
  }),
});
export type DeleteSupplierOrderItemsRequest = z.infer<typeof DeleteSupplierOrderItemsRequestSchema>;

export const DeleteSupplierOrderItemsResponseSchema = BaseVoidResponseSchema;
export type DeleteSupplierOrderItemsResponse = z.infer<
  typeof DeleteSupplierOrderItemsResponseSchema
>;

export const CancelSupplierOrderItemsRequestSchema = z.object({
  orderId: z.string(),
  body: z.object({
    itemId: z.array(z.string()),
  }),
});
export type CancelSupplierOrderItemsRequest = z.infer<typeof CancelSupplierOrderItemsRequestSchema>;

export const CancelSupplierOrderItemsResponseSchema = BaseVoidResponseSchema;
export type CancelSupplierOrderItemsResponse = z.infer<
  typeof CancelSupplierOrderItemsResponseSchema
>;

export const DeleteSupplierOrderMaterialBasketItemsRequestSchema = z.object({orderId: z.string()});
export type DeleteSupplierOrderMaterialBasketItemsRequest = z.infer<
  typeof DeleteSupplierOrderMaterialBasketItemsRequestSchema
>;

export const DeleteSupplierOrderMaterialBasketItemsResponseSchema = BaseVoidResponseSchema;
export type DeleteSupplierOrderMaterialBasketItemsResponse = z.infer<
  typeof DeleteSupplierOrderMaterialBasketItemsResponseSchema
>;

/**
 * PUT requests
 */
const SupplierOrderItemChangedFieldSchema = z.enum([
  'supplierQuantity',
  'warehouseRequestedQuantity',
  'supplierUnitPrice',
  'warehouseQuantity',
  'supplierQuantityMapping',
  'warehouseQuantityMapping',
]);
export type SupplierOrderItemChangedField = z.infer<typeof SupplierOrderItemChangedFieldSchema>;

export const PutSupplierOrderItemCalculationRequestSchema = z.object({
  orderItemId: z.string(),
  body: z.object({
    changedField: z.object({
      field: SupplierOrderItemChangedFieldSchema,
    }),
    orderDetail: z.object({
      supplierQuantity: z.number().nullish(),
      supplierUnitPrice: z.number().nullish(),
    }),
    requestDetail: z.object({
      warehouseRequestedQuantity: z.number().nullish(),
      warehouseUnitPurchasePrice: z.number().nullish(),
    }),
    supplierArticle: z.object({
      supplierQuantity: z.number().nullish(),
      warehouseQuantity: z.number().nullish(),
    }),
  }),
  signal: AbortSignalSchema,
});
export type PutSupplierOrderItemCalculationRequest = z.infer<
  typeof PutSupplierOrderItemCalculationRequestSchema
>;

export const PutSupplierOrderItemCalculationResponseSchema = z.object({
  orderDetail: z.object({
    supplierQuantity: z.number(),
    supplierUnitPrice: z.number(),
    supplierTotalPrice: z.number(),
  }),
  requestDetail: z.object({
    warehouseRequestedQuantity: z.number(),
    warehouseUnitPurchasePrice: z.number(),
  }),
  supplierArticle: z.object({
    supplierQuantity: z.number(),
    warehouseQuantity: z.number(),
  }),
});
export type PutSupplierOrderItemCalculationResponse = z.infer<
  typeof PutSupplierOrderItemCalculationResponseSchema
>;

export const PutSupplierOrderHeaderActionRequestSchema = z.object({
  orderId: z.string(),
  body: z.object({
    actionKey: z.string(),
    normalizePromptResponseCode: z.enum(['NORMALIZE', 'DONT']).nullish(),
  }),
});
export type PutSupplierOrderHeaderActionRequest = z.infer<
  typeof PutSupplierOrderHeaderActionRequestSchema
>;

export const PutSupplierOrderHeaderActionResponseSchema = z.object({normalizePrompt: z.boolean()});

export type PutSupplierOrderHeaderActionResponse = z.infer<
  typeof PutSupplierOrderHeaderActionResponseSchema
>;
